<script lang="ts" setup>
import _ from 'lodash'
import { computed, type Ref, ref } from 'vue'

import { filterStructuredDocument } from '../../components/health-data/utils'
import dayjs from '../../misc/dayjs'
import { getDrugEventPrestationTypeLabel } from '../../misc/DrugEventHelper'
import { type DrugEvent, DrugEventPrestationType, DrugEventType } from '../../types/health-data'

export interface DrugEventsViewerProps {
  drugEvents: DrugEvent[]
  activeIds: number[]
  drugEventPrestationType: DrugEventPrestationType | null
}

const props = defineProps<DrugEventsViewerProps>()

const filtering = ref(null) as Ref<string>
const filteringOptions = {
  threshold: 0.49,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['drug.activeSubstance', 'drug.atc', 'drug.atcLabel', 'drug.commercialName', 'drug.shortUcd'],
}
const filteredDrugEvents = computed(() => filterStructuredDocument(props.drugEvents, filtering.value, filteringOptions))
const sortedDrugEvents = computed(() =>
  _.orderBy(filteredDrugEvents.value, ['creationDate', 'drug.atc', 'drug.shortUcd'], ['desc', 'asc', 'asc']),
)

function isHighlighted(drugEvent: DrugEvent): boolean {
  return Boolean(
    props.activeIds.includes(drugEvent.id)
    || (drugEvent.matchOrigins
    && ((drugEvent.matchOrigins.cohortNodeIds && drugEvent.matchOrigins.cohortNodeIds.length)
    || (drugEvent.matchOrigins.savedSearchIds && drugEvent.matchOrigins.savedSearchIds.length))),
  )
}
</script>

<template>
  <div>
    <div class="card-body row">
      <h1 class="report-title col">
        {{ getDrugEventPrestationTypeLabel(drugEventPrestationType) }}
      </h1>
      <div class="col-auto form-group">
        <input
          v-model="filtering"
          type="text"
          placeholder="Filtrage rapide"
          class="form-control col-auto"
        >
      </div>
    </div>
    <div class="card-body table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Code ATC</th>
            <th>Code UCD</th>
            <th>Nom commercial</th>
            <th>Dose</th>
            <template v-if="drugEventPrestationType !== null">
              <th>Indication</th>
              <th>Validation initiale</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="drugEvent in sortedDrugEvents"
            :key="drugEvent.id"
            :class="{ 'hinted-line-highlighted': isHighlighted(drugEvent) }"
          >
            <td>{{ drugEvent.creationDate ? dayjs(drugEvent.creationDate).format('L') : '-' }}</td>
            <td>{{ DrugEventType[drugEvent.type] || '-' }}</td>
            <td>{{ drugEvent.drug.atc || '-' }}</td>
            <td>{{ drugEvent.drug.shortUcd || '-' }}</td>
            <td>{{ drugEvent.drug.commercialName || '-' }}</td>
            <td>{{ drugEvent.amount || '-' }}</td>
            <template v-if="drugEventPrestationType !== null">
              <td>{{ drugEvent.indication || '-' }}</td>
              <td>
                {{
                  drugEvent.prescriptionValidation !== null ? (drugEvent.prescriptionValidation ? 'OUI' : 'NON') : '-'
                }}
              </td>
            </template>
          </tr>
          <tr v-if="!sortedDrugEvents.length">
            Aucun résultat ne correspond au filtre "{{
              filtering
            }}"
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
